import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { memo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDataStore } from '@/stores/dataStore';
import { TeamTag } from '@/components/TeamTag';
import type { Message } from '@/types/api';
import { formatMessageMeta, formatTime } from '@/lib/format';
import type { RowItem } from './messageRows';
import type { SavedChannelKind } from '@/lib/savedMessages';
import { openForwardModal } from '@/components/ForwardModal';
import { openReportModal } from '@/components/ReportModal';
import { parseForwarded } from '@/lib/forwarded';
import { standaloneEmojiCount } from '@/lib/emojiMessage';
import { PollBubble } from '@/components/PollBubble';
import { TaskListBubble } from '@/components/TaskListBubble';
import { ChatBubbleIcon, PinIcon } from '@/components/icons';
import { AttachmentList, parseAttachments } from '@/components/AttachmentList';
import { UserCardTrigger } from '@/components/userCard/UserCardTrigger';
import { DaySeparator, UnreadSeparator, ReadReceipt } from './messageSeparators';
import { MessageContextMenu } from './MessageContextMenu';
import { useMessageContextMenu } from './useMessageContextMenu';
import { ShelfPickerModal } from '@/components/vault/ShelfPickerModal';
import { MessageBody, ReactionRow, ReplyQuote, Toolbar } from './messageBubbleParts';
import { emitAppEvent } from '@/lib/appEvents';
import { EditForm } from './EditForm';
import { systemMessageText } from '@/lib/systemMessage';

/**
 * Renders one virtualized row. Pulled out into its own component so each
 * row's hook state (context-menu, reaction picker) doesn't get torn down
 * when neighbours scroll in/out of view — virtualizer remounts components
 * once they leave the overscan window, and that's fine for static
 * separators but would lose in-progress picker state for messages.
 */
function VirtualRowImpl(props: {
  item: RowItem;
  editRequest?: { id: string; version: number } | null;
  onEditRequestHandled?: () => void;
  myId: string | null;
  channelLabel: string;
  channelKind: SavedChannelKind;
  commentsAllowed: boolean;
  reactionNameFor: (userId: string) => string | undefined;
  onReply: (m: Message) => void;
  onEdit: (m: Message, content: string) => Promise<void>;
  onDelete: (m: Message) => Promise<void>;
  onDeleteForMe: (m: Message) => Promise<void>;
  onTogglePin: (m: Message) => Promise<void>;
  onReact: (m: Message, emoji: string) => Promise<void>;
  canModerate: boolean;
  /** May the current user pin messages here (see canPinIn)? */
  canPin: boolean;
  authorBanned: boolean;
  /** This channel is the personal Сейф — render forwards compactly. */
  isVault?: boolean;
}) {
  const { item } = props;
  const { t } = useTranslation();
  // Peer's read timestamp for a 1:1 DM — drives the ✓/✓✓ on our own bubbles.
  // Selected before the early returns (hooks must run unconditionally).
  const peerReadAt = useDataStore((s) => {
    if (props.item.kind !== 'message' || props.channelKind !== 'DM') return null;
    const msg = props.item.message;
    const ch = s.dmChannels[msg.channelId];
    if (!ch || ch.type !== 'DM') return null;
    const peer = ch.directParticipants.find((p) => p.userId !== props.myId);
    if (!peer) return null;
    return s.readMarkersByChannel[msg.channelId]?.[peer.userId]?.updatedAt ?? null;
  });
  if (item.kind === 'day') return <DaySeparator date={item.date} unread={item.unread} />;
  if (item.kind === 'unread') return <UnreadSeparator />;
  const m = item.message;
  // Service messages (task-completion notes etc.) render as a centered grey
  // line — no avatar, bubble, toolbar or reactions.
  if (m.system) {
    return (
      <div className="my-1 flex justify-center px-4">
        <span className="rounded-full bg-default/40 px-3 py-1 text-center text-xs text-muted">
          {systemMessageText(m, t)}
        </span>
      </div>
    );
  }
  return (
    <MessageRow
      message={m}
      editRequestVersion={props.editRequest?.id === m.id ? props.editRequest.version : 0}
      onEditRequestHandled={props.onEditRequestHandled}
      mine={m.authorId === props.myId}
      mentioned={!!props.myId && m.mentions.includes(props.myId)}
      peerReadAt={peerReadAt}
      commentsAllowed={props.commentsAllowed}
      continueGroup={item.continueGroup}
      channelLabel={props.channelLabel}
      channelKind={props.channelKind}
      reactionNameFor={props.reactionNameFor}
      onReply={() => props.onReply(m)}
      onEdit={(content) => props.onEdit(m, content)}
      onDelete={() => props.onDelete(m)}
      onDeleteForMe={() => props.onDeleteForMe(m)}
      onTogglePin={() => props.onTogglePin(m)}
      onReact={(emoji) => props.onReact(m, emoji)}
      canModerate={props.canModerate}
      canPin={props.canPin}
      authorBanned={props.authorBanned}
      isVault={props.isVault}
    />
  );
}

/**
 * Memoized: MessagesList re-renders on store activity unrelated to any single
 * row (typing, presence, scroll-driven setShowJump, …). With the parent now
 * passing referentially-stable callbacks, a memoized row skips
 * those re-renders and only updates when its own `item` / display props change.
 * Internal store subscriptions (peerReadAt here, cardGuildId in MessageRow)
 * still fire, so read receipts and the user-card guild context stay live.
 */
export const VirtualRow = memo(VirtualRowImpl);

function MessageRow(props: {
  message: Message;
  editRequestVersion?: number;
  onEditRequestHandled?: () => void;
  mine: boolean;
  /** Current user is explicitly mentioned in this message. */
  mentioned?: boolean;
  /** Peer's read timestamp (1:1 DM only) — render ✓✓ when it covers this msg. */
  peerReadAt?: string | null;
  /** Whether comment-threads are allowed here (off in 1:1 DM / when toggled). */
  commentsAllowed: boolean;
  continueGroup: boolean;
  /** Cached channel name written into the saved-message bookmark
   *  payload — the saved view renders it offline without re-resolving
   *  the channel. */
  channelLabel: string;
  channelKind: SavedChannelKind;
  /** Resolve a reactor's id → display name for the reaction tooltip. */
  reactionNameFor?: (userId: string) => string | undefined;
  onReply: () => void;
  onEdit: (content: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onDeleteForMe: () => Promise<void>;
  onTogglePin: () => Promise<void>;
  onReact: (emoji: string) => Promise<void>;
  /** May the current user delete OTHER people's messages here (MANAGE_MESSAGES)? */
  canModerate?: boolean;
  /** May the current user pin messages here? */
  canPin?: boolean;
  /** Is this message's author banned from the current server? */
  authorBanned?: boolean;
  /** This channel is the personal Сейф — render forwards compactly. */
  isVault?: boolean;
}) {
  const { t } = useTranslation();
  const m = props.message;
  // In the personal Сейф a saved-from-someone message is stored as a forward I
  // sent. Detect it early so the row can attribute to the ORIGINAL author
  // (avatar/name) and not collapse distinct authors into one "my" group.
  const vaultFwd = props.isVault ? parseForwarded(m.content) : null;
  const [editing, setEditing] = useState(false);
  const { editRequestVersion, mine, onEditRequestHandled } = props;
  useEffect(() => {
    if (editRequestVersion && mine) { setDraft(m.content); setEditing(true); onEditRequestHandled?.(); }
  }, [editRequestVersion, mine, m.content, onEditRequestHandled]);

  // The edit field unmounts with focus in it; without this the caret fell to
  // <body> and typing went nowhere. After the commit (an effect), so a save
  // that resolves late still finds the field gone.
  const wasEditing = useRef(false);
  useEffect(() => {
    if (wasEditing.current && !editing) emitAppEvent('composer:focus', { channelId: m.channelId });
    wasEditing.current = editing;
  }, [editing, m.channelId]);

  // Banned author's messages are collapsed (mods can reveal to read them).
  const [revealBanned, setRevealBanned] = useState(false);
  const [draft, setDraft] = useState(m.content);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { contextMenu, openContextMenu, openContextMenuAt, longPress, closeContextMenu, copyText, copyLink, saveToVault } =
    useMessageContextMenu(m, props.mine);
  // "На полку" — the shelf picker for this saved vault message (Сейф only).
  const [shelfPickerOpen, setShelfPickerOpen] = useState(false);

  // Telegram-style alignment: own right, others left.
  //
  // Bubbles carry a glass backdrop. Uniform 18px radius on all four
  // corners — the asymmetric "speech-origin" corner from the prior
  // pass read as off-balance, not premium. Symmetric shape, premium
  // surface (glass + blur + inset highlight).
  //
  // Bg opacity: foreign rgba(23,23,23,0.55) keeps text legible over the
  // dot field; mine carries a faint #00F0FF tint (0.07 alpha) plus a
  // cyan inset ring so the chroma differs from neutral.
  // Channel messages share a reading column. Personal messages are offset
  // through data-direct/data-own without changing message permissions.
  const align = 'justify-start';
  // Media-only message (no text/poll/task, only inline images) → drop the
  // bubble frame and overlay the time + ticks on the photo (Telegram-style).
  const attachmentsParsed = parseAttachments(m.attachments);
  const emojiOnlyCount = !editing && !m.poll && !m.taskList && !m.replyTo &&
    !vaultFwd && !props.authorBanned && attachmentsParsed.length === 0
    ? standaloneEmojiCount(m.content)
    : 0;
  const mediaOnly =
    !m.content.trim() &&
    !m.poll &&
    !m.taskList &&
    attachmentsParsed.length > 0 &&
    attachmentsParsed.every((a) => a.mime.startsWith('image/') && !a.asFile);
  // A message with nothing renderable (no poll/task/text and no parseable
  // attachment) would otherwise paint an empty bubble with just a timestamp,
  // which reads as broken. Show a muted placeholder instead — and distinguish a
  // dropped/legacy attachment (raw array present but it failed strict parsing)
  // from a genuinely empty message.
  const rawAttachmentCount = Array.isArray(m.attachments) ? (m.attachments as unknown[]).length : 0;
  const isEmptyBubble =
    !m.poll && !m.taskList && !m.content.trim() && attachmentsParsed.length === 0;
  // Telegram-tight: a hair between same-author messages, a small gap between
  // groups (was mt-4 — too airy, made the feed feel sparse/oversized).
  // A vault forward is its own group: consecutive saves are all "by me" so the
  // default grouping would collapse distinct original authors into one run and
  // hide their avatars. Force each saved-from-someone message to stand alone.
  const continued = props.continueGroup && !vaultFwd && !m.replyTo;
  const createdAt = new Date(m.createdAt);

  // Avatar gutter shows for the first message of every author's group.
  // Inside a continuation group we keep the gutter width but render no avatar.
  const showAvatar = !continued;

  // Pending state — bubble was just inserted optimistically and is still
  // waiting for the server roundtrip. We dim the row 40% so it reads as
  // "sending", but never block interaction (the bubble will swap to the
  // real one as soon as api.sendMessage() resolves — usually <150ms).
  const isPending = m.id.startsWith('tmp-');

  // Clicking the author's avatar or name opens the shared user card. In a
  // guild channel the card resolves roles via the active guild id; in a DM
  // (currentGuildId === null) it shows the basic profile.
  const cardGuildId = useDataStore((s) => s.currentGuildId);
  const authorUser = {
    id: m.author.id,
    username: m.author.username,
    displayName: m.author.displayName,
    avatarUrl: m.author.avatarUrl,
  };

  // Show the ORIGINAL author (name + snapshotted avatar) for a vault forward,
  // else the real message author.
  const displayName = vaultFwd?.authorName || m.author.displayName || m.author.username;
  const displayAvatarUrl = vaultFwd ? vaultFwd.authorAvatarUrl ?? null : m.author.avatarUrl;
  // Orb / hue fallback key: seed from the original author's id (carried in the
  // forward) so a photo-less author keeps the SAME orb colour as everywhere
  // else; fall back to the name only for legacy forwards without an id.
  const displayAvatarSeed = vaultFwd ? vaultFwd.authorId || vaultFwd.authorName : m.author.id;

  return (
    <div
      className={
        `group relative flex ${align} select-text-fine chat-row` +
        (isPending ? ' opacity-60' : '')
      }
      data-message-id={m.id}
      data-own={props.mine || undefined}
      data-direct={props.channelKind === 'DM' || props.channelKind === 'GROUP_DM' || undefined}
      data-continued={continued || undefined}
      data-mentioned={props.mentioned || undefined}
      data-has-reply={!!m.replyTo || undefined}
      data-emoji-only={emojiOnlyCount || undefined}
      onContextMenu={openContextMenu}
      {...longPress}
    >
      {/* Avatar gutter, top-aligned rather than bottom. The avatar identifies
          the author, and the author's name is the FIRST line of the bubble —
          but `self-end` pinned it a fixed distance from the row's BOTTOM, so it
          drifted down as the message grew. Measured on a real thread: 14px from
          the top on a one-liner, 92px on a message carrying a quote — level
          with the body text, two lines below the name it belongs to. mt-1 sets
          the 32px circle optically on the name line. */}
      {(
        <div className="chat-avatar-gutter shrink-0 self-start">
          {/* Continuation lines carry no header, so their time appears in the
              gutter on hover: the Slack/Linear pattern that keeps every line
              clean without hiding when it was sent. */}
          {!showAvatar && props.channelKind !== 'DM' && props.channelKind !== 'GROUP_DM' && (
            <time
              dateTime={m.createdAt}
              title={formatMessageMeta(createdAt)}
              className="chat-continuation-time"
            >
              {formatTime(createdAt)}
            </time>
          )}
          {showAvatar && (
            <UserCardTrigger
              user={authorUser}
              guildId={cardGuildId}
              label={t('messages.profileOf', { name: authorUser.displayName || authorUser.username })}
              className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
            >
            <IdentityAvatar id={displayAvatarSeed} name={displayName || '?'} src={displayAvatarUrl} className="chat-avatar" />
            </UserCardTrigger>
          )}
        </div>
      )}

      {contextMenu && (
        <MessageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          mine={props.mine}
          pinned={!!m.pinned}
          saved={false}
          onClose={closeContextMenu}
          onReply={() => {
            closeContextMenu();
            props.onReply();
          }}
          showThread={props.commentsAllowed}
          showPin={!!props.canPin}
          onOpenThread={() => {
            closeContextMenu();
            emitAppEvent('thread:open', { messageId: m.id });
          }}
          onToggleSave={() => {
            closeContextMenu();
            void saveToVault();
          }}
          onAddToShelf={
            props.isVault
              ? () => {
                  closeContextMenu();
                  setShelfPickerOpen(true);
                }
              : undefined
          }
          onRemind={() => {
            closeContextMenu();
            emitAppEvent('remind:open', { messageId: m.id });
          }}
          onForward={() => {
            closeContextMenu();
            openForwardModal({
              messageId: m.id,
              sourceChannelId: m.channelId,
              sourceChannelLabel: props.channelLabel,
              authorName: m.author.displayName || m.author.username,
              content: m.content,
              attachmentIds: parseAttachments(m.attachments).map((a) => a.id),
            });
          }}
          onTogglePin={async () => {
            closeContextMenu();
            await props.onTogglePin();
          }}
          onEdit={() => {
            closeContextMenu();
            setEditing(true);
          }}
          onDelete={async () => {
            closeContextMenu();
            await props.onDelete();
          }}
          onDeleteForMe={async () => {
            closeContextMenu();
            await props.onDeleteForMe();
          }}
          onCopyText={async () => {
            closeContextMenu();
            await copyText();
          }}
          onCopyLink={async () => {
            closeContextMenu();
            await copyLink();
          }}
          onReact={async (emoji) => {
            await props.onReact(emoji);
          }}
          onReport={() => {
            closeContextMenu();
            openReportModal({
              kind: 'message',
              targetId: m.id,
              targetName: m.author.displayName || m.author.username,
            });
          }}
          canModerate={props.canModerate}
        />
      )}

      {shelfPickerOpen && (
        <ShelfPickerModal messageId={m.id} onClose={() => setShelfPickerOpen(false)} />
      )}

      <div className="relative flex flex-col chat-content min-w-0 flex-1">
        <div
          className={mediaOnly ? 'relative' : 'text-foreground relative'}
          // Belt-and-suspenders: an onContextMenu on the bubble itself
          // in addition to the row above. A few users in the friends-
          // test pass reported the row-level handler not firing on
          // certain bubble paths (markdown links, embed previews); this
          // catches contextmenu before any child element can swallow it.
          // openContextMenu is idempotent — calling twice from row + bubble
          // is harmless, the menu just opens at the second-fired position.
          onContextMenu={openContextMenu}
        >
          {!continued && props.channelKind !== 'DM' && (
            <div className="chat-author-line flex min-w-0 items-baseline gap-2">
              <UserCardTrigger
                user={authorUser}
                guildId={cardGuildId}
                label={t('messages.profileOf', { name: authorUser.displayName || authorUser.username })}
                className="chat-author block min-w-0 truncate text-left outline-none hover:underline focus-visible:underline"
              >
                <span>
                  {m.author.displayName || m.author.username}
                </span>
              </UserCardTrigger>
              <TeamTag tag={m.author.teamTag} className="chat-team-tag" />
              {props.channelKind !== 'GROUP_DM' && !mediaOnly && <time
                dateTime={m.createdAt}
                title={formatMessageMeta(createdAt)}
                className="chat-time shrink-0 tabular-nums"
              >
                {formatTime(createdAt)}
              </time>}
              {props.authorBanned && (
                <span className="font-medium shrink-0 rounded-full bg-danger/15 px-1.5 py-0.5 text-[10px] text-danger-ink">
                  {t('messages.bannedBadge')}
                </span>
              )}
            </div>
          )}

          {m.replyTo && (
            <ReplyQuote
              target={m.replyTo}
              mine={false}
            />
          )}

          {/* Content + meta share one row: time tucks to the bottom-right of
              the message (Telegram-style) instead of taking its own line. */}
          {/* flex-wrap so the reaction row below can claim a full line of its
              own instead of squeezing in beside the timestamp. */}
          <div className="flex flex-wrap items-end gap-x-2">
            <div className="min-w-0 flex-1">
              {props.authorBanned && !revealBanned ? (
                <button
                  type="button"
                  onClick={() => setRevealBanned(true)}
                  className="block w-full rounded-lg bg-white/[0.04] px-3 py-2 text-left text-[12px] italic text-ink-muted transition-colors hover:bg-white/[0.07]"
                >
                  {t('messages.bannedHidden')}
                </button>
              ) : editing ? (
                <EditForm
                  draft={draft}
                  setDraft={setDraft}
                  originalContent={m.content}
                  onSave={async (next) => {
                    await props.onEdit(next);
                    setEditing(false);
                  }}
                  onCancel={() => {
                    setDraft(m.content);
                    setEditing(false);
                  }}
                />
              ) : (
                <>
                  {/* Poll messages: question is in m.content; PollBubble
                      renders it as the header. Skip the markdown body to
                      avoid double-rendering the question line. */}
                  {m.poll ? (
                    <PollBubble
                      poll={m.poll}
                      messageId={m.id}
                      channelId={m.channelId}
                      messageAuthorId={m.authorId}
                    />
                  ) : m.taskList ? (
                    <TaskListBubble
                      taskList={m.taskList}
                      messageId={m.id}
                      channelId={m.channelId}
                      messageAuthorId={m.authorId}
                    />
                  ) : (
                    m.content.trim().length > 0 && (
                      <MessageBody content={m.content} mine={false} channelId={m.channelId} compactForward={props.isVault} emojiOnlyCount={emojiOnlyCount} />
                    )
                  )}
                  <AttachmentList
                    attachments={attachmentsParsed}
                    metaOverlay={
                      mediaOnly ? (
                        <span className="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded-full bg-black/55 px-1.5 py-0.5 text-xs tabular-nums text-white">
                          <span>{formatTime(createdAt)}</span>
                          {props.mine && props.channelKind === 'DM' && (
                            <ReadReceipt readAt={props.peerReadAt ?? null} createdAt={m.createdAt} />
                          )}
                        </span>
                      ) : undefined
                    }
                  />
                  {isEmptyBubble && (
                    <span className="text-[12px] italic text-ink-muted">
                      {rawAttachmentCount > 0
                        ? t('messages.attachmentUnavailable')
                        : t('messages.emptyMessage')}
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Feed: the time lives in the header (or the gutter), so only the
                small facts that belong to THIS line stay beside it. */}
            {!editing && !mediaOnly && (m.editedAt || m.pinned || props.channelKind === 'DM' || props.channelKind === 'GROUP_DM') && (
              <div className="flex shrink-0 select-none items-center gap-1.5 pb-px text-[11px] leading-5 text-ink-muted">
                {(props.channelKind === 'DM' || props.channelKind === 'GROUP_DM') && <time dateTime={m.createdAt} title={formatMessageMeta(createdAt)}>{formatTime(createdAt)}</time>}
                {m.editedAt && <span>{t('messages.edited')}</span>}
                {m.pinned && (
                  <span title={t('messages.pinned')}>
                    <PinIcon className="h-3 w-3" />
                  </span>
                )}
                {props.mine && props.channelKind === 'DM' && (
                  <ReadReceipt readAt={props.peerReadAt ?? null} createdAt={m.createdAt} />
                )}
              </div>
            )}
            {/* Reactions live INSIDE the bubble, under the text — the way every
                messenger people already use does it. As a sibling AFTER the
                bubble they hung in open space: on my own right-aligned messages
                a lone pill floated with nothing behind it, and on other
                people's they read as a separate strip that visually belonged to
                the next message. */}
            {m.reactions.length > 0 && (
              <ReactionRow
                reactions={m.reactions}
                onReact={props.onReact}
                mine={props.mine}
                nameFor={props.reactionNameFor}
              />
            )}
          </div>

          {/* Comment-thread affordance — root messages with replies, never in
              1:1 DMs (the badge is hidden there even for legacy threads).
              Telegram-style: avatar stack of recent commenters + the count. */}
          {props.channelKind !== 'DM' && (m.threadReplyCount ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => emitAppEvent('thread:open', { messageId: m.id })}
              className="group/th mt-1 inline-flex items-center gap-2 self-start rounded-full bg-primary/10 py-1 pl-1.5 pr-2.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
            >
              {m.threadParticipants && m.threadParticipants.length > 0 ? (
                <span className="flex -space-x-1.5">
                  {m.threadParticipants.slice(0, 3).map((p) => (
                    <IdentityAvatar key={p.id} id={p.id} name={p.displayName || p.username} src={p.avatarUrl} size="xs" />
                  ))}
                </span>
              ) : (
                <ChatBubbleIcon className="h-3.5 w-3.5" />
              )}
              <span>
                {t('messages.comments', { count: m.threadReplyCount ?? 0 })}
              </span>
            </button>
          )}
        </div>

      <div className="chat-action-lane"><Toolbar
        mine={false}
        pickerOpen={pickerOpen}
        onOpenPicker={() => setPickerOpen(true)}
        onClosePicker={() => setPickerOpen(false)}
        onReact={props.onReact}
        onReply={props.onReply}
        onForward={() => openForwardModal({
          messageId: m.id, sourceChannelId: m.channelId, sourceChannelLabel: props.channelLabel,
          authorName: m.author.displayName || m.author.username, content: m.content,
          attachmentIds: attachmentsParsed.map((a) => a.id),
        })}
        onMore={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          openContextMenuAt(rect.right, rect.bottom);
        }}
      /></div>
      </div>
    </div>
  );
}
